import React from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import store from './store';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import OrderConfirm from './pages/OrderConfirm';
import OrderSuccess from './pages/OrderSuccess';
import PaymentSuccess from './pages/PaymentSuccess';
import Orders from './pages/Orders';
import SettingsPage from './components/SettingsPage';
import ChangePassword from './pages/ChangePassword';
import SupportPage from './components/SupportPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Disclaimer from './pages/Disclaimer';
import { Compass } from 'lucide-react';
import PageHeading from './components/PageHeading';
import './styles/site.css';
import ScrollToTop from './components/ScrollToTop';
import CustomerService from './pages/CustomerService';
import AfterSales from './pages/AfterSales';
import Invoice from './pages/Invoice';
import AntiBanGuide from './pages/AntiBanGuide';
import UpgradeGuide from './pages/UpgradeGuide';
import DocumentTitleManager from './components/DocumentTitleManager';

/**
 * 布局组件 - 包含Header和Footer
 */
function Layout({ children }) {
  const { pathname } = useLocation();
  return (
    <div className={`App flex flex-col min-h-screen ${pathname === '/order/confirm' ? 'has-checkout' : ''}`}>
      <Header />
      <main className={`flex-grow ${pathname === '/' ? '' : 'site-page'}`}>
        {children}
      </main>
      <Footer />
    </div>
  );
}

/**
 * 404页面组件
 */
function NotFoundPage() {
  const { t } = useTranslation();

  return (
    <div className="page-shell">
      <PageHeading Icon={Compass} eyebrow="404 · 页面未找到" title={t('errors.pageNotFound', '页面不存在')} description={t('errors.pageNotFoundDesc', '抱歉，您访问的页面不存在。')} />
      <div className="page-card text-center py-16">
        <p className="text-7xl font-bold text-brand mb-6">404</p>
        <p className="text-muted mb-8">回到首页选购，或让大师兄帮你找到需要的服务。</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/" className="brand-purchase">{t('navigation.backToHome', '返回首页')}</Link>
          <Link to="/customer-service" className="page-secondary">联系客服</Link>
        </div>
      </div>
    </div>
  );
}

/**
 * 主应用组件
 */
function App() {
  return (
    <Provider store={store}>
      <Router>
        <ScrollToTop />
        <DocumentTitleManager />
        <Layout>
          <Routes>
            {/* 首页 */}
            <Route path="/" element={<Home />} />

            {/* 订单相关页面 */}
            <Route path="/order/confirm" element={<OrderConfirm />} />
            <Route path="/order/success" element={<OrderSuccess />} />
            <Route path="/detail/plus-upgrade" element={<PaymentSuccess />} />
            <Route path="/orders" element={<Orders />} />

            {/* 用户相关页面 */}
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/customer-service" element={<CustomerService />} />
            <Route path="/after-sales" element={<AfterSales />} />
            <Route path="/after-sales/invoice" element={<Invoice />} />

            {/* 内容页面 */}
            <Route path="/anti-ban-guide" element={<AntiBanGuide />} />
            <Route path="/upgrade-guide" element={<UpgradeGuide />} />

            {/* 法律页面 */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />

            {/* 404页面 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
      </Router>
    </Provider>
  );
}

export default App;
